import React, { useState, useEffect } from "react";
import { api } from "../api.js";
import { AnnouncementBanner } from "./AnnouncementBanner.js";
import { AnnouncementToast } from "./AnnouncementToast.js";
import { CARD_SHADOW } from "./ui.js";
import { usagePeriodLabel } from "./usagePeriod.js";

const STATUSES = [
  { key: "completed", label: "締結完了", color: "#1D9E75", bg: "#E1F5EE", text: "#0F6E56" },
  { key: "sent", label: "送信済み", color: "#378ADD", bg: "#E6F1FB", text: "#185FA5" },
  { key: "signed", label: "顧客署名済", color: "#7F77DD", bg: "#EEEDFE", text: "#534AB7" },
  { key: "draft", label: "下書き", color: "#888780", bg: "#F1EFE8", text: "#5F5E5A" },
  { key: "declined", label: "却下", color: "#E24B4A", bg: "#FCEBEB", text: "#A32D2D" },
  { key: "expired", label: "期限切れ", color: "#BA7517", bg: "#FAEEDA", text: "#854F0B" },
  // サーバーが書く状態はすべて載せる。以前は not_found / canceled が無く「下書き」と表示され、
  // 追跡が止まった書類に気づけなかった（services/polling.ts・routes/cloudsign.ts の取り消し）
  { key: "not_found", label: "取得不可", color: "#5F5E5A", bg: "#EDEBE5", text: "#3F3E3B" },
  { key: "canceled", label: "取り消し", color: "#E24B4A", bg: "#FCEBEB", text: "#A32D2D" },
];

/**
 * レイアウトの基準値（色は変えない）。
 *   - カードどうしの間隔は縦横とも GAP に統一
 *   - カードの余白と角丸はすべて CARD_PADDING / CARD_RADIUS に統一（塗りのカードも枠線のカードも同じ形）
 *   - 12 列のグリッドに載せ、指標（3 列ずつ）・使用量とステータス（6 列ずつ）・リンク（4 列ずつ）の端を揃える
 */
const GAP = 16;
const CARD_PADDING = "20px 24px";
const CARD_RADIUS = 12;

const card: React.CSSProperties = {
  background: "white",
  border: "1px solid #e8e7e3",
  borderRadius: CARD_RADIUS,
  padding: CARD_PADDING,
  boxSizing: "border-box",
  minWidth: 0,
  boxShadow: CARD_SHADOW,
};

/** 塗りのカード（指標・リンク）。枠線は透明にして、枠線のカードと外寸を揃える */
const filledCard: React.CSSProperties = {
  background: "#f5f4f0",
  border: "1px solid transparent",
  borderRadius: CARD_RADIUS,
  padding: CARD_PADDING,
  boxSizing: "border-box",
  minWidth: 0,
};

const grid12: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: GAP };
const span = (n: number): React.CSSProperties => ({ gridColumn: "span " + n });

/** カードの見出し。右側に操作を置いても高さが変わらないよう、行の高さを固定する */
function CardTitle({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: 24, marginBottom: 16 }}>
      <div style={{ fontSize: 16, fontWeight: 600 }}>{children}</div>
      {action}
    </div>
  );
}

function Pill({ status }: { status: string }) {
  // 未知の状態を「下書き」に見せない。そのままの値を出す
  const s = STATUSES.find((x) => x.key === status) || { label: status, bg: "#F1EFE8", text: "#5F5E5A" };
  return <span style={{ display: "inline-block", padding: "3px 12px", borderRadius: 12, fontSize: 13, fontWeight: 500, background: s.bg, color: s.text }}>{s.label}</span>;
}

function MetricCard({ label, value, sub, positive }: { label: string; value: string | number; sub?: string; positive?: boolean }) {
  // 見出し・数値・前月比の行の高さを固定し、前月比が無いカードでも数値の位置を揃える
  return (
    <div style={{ ...filledCard, display: "flex", flexDirection: "column", gap: 8, height: "100%" }}>
      <div style={{ fontSize: 14, lineHeight: "20px", color: "#6b6b68" }}>{label}</div>
      <div style={{ fontSize: 28, lineHeight: "36px", fontWeight: 600 }}>{value}</div>
      <div style={{ fontSize: 13, lineHeight: "18px", minHeight: 18, color: positive ? "#1D9E75" : "#E24B4A" }}>{sub ?? ""}</div>
    </div>
  );
}

function GaugeRing({ used, limit }: { used: number; limit: number }) {
  const pct = limit > 0 ? Math.min(used / limit, 1) : 0;
  const r = 54;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;
  const gap = circ - dash;
  const color = pct >= 1 ? "#E24B4A" : pct >= 0.9 ? "#BA7517" : "#7F77DD";
  return (
    <div style={{ position: "relative", width: 140, height: 140, flexShrink: 0 }}>
      <svg width="140" height="140" viewBox="0 0 140 140" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="70" cy="70" r={r} fill="none" stroke="#e8e7e3" strokeWidth="12" />
        <circle cx="70" cy="70" r={r} fill="none" stroke={color} strokeWidth="12" strokeDasharray={`${dash} ${gap}`} strokeLinecap="round" />
      </svg>
      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
        <div style={{ fontSize: 28, fontWeight: 600 }}>{Math.round(pct * 100)}%</div>
        <div style={{ fontSize: 13, color: "#6b6b68" }}>{used} / {limit}</div>
      </div>
    </div>
  );
}

function AlertBanner({ used, limit }: { used: number; limit: number }) {
  if (limit <= 0) return null;
  const pct = used / limit;
  if (pct < 0.7) return null;
  let msg: string, bg: string, color: string;
  if (pct >= 1) { msg = `月間上限 ${limit} 件に到達しました。追加送信にはプラン変更が必要です。`; bg = "#FCEBEB"; color = "#A32D2D"; }
  else if (pct >= 0.9) { msg = `今回の請求期間の送信件数が上限の 90% に到達しました。残り ${limit - used} 件です。`; bg = "#FAEEDA"; color = "#854F0B"; }
  else { msg = `今回の請求期間の送信件数が上限の 70% に到達しました。プランの見直しは「プラン・請求」から行えます。`; bg = "#FAEEDA"; color = "#854F0B"; }
  return (
    <div style={{ padding: "12px 16px", borderRadius: CARD_RADIUS, fontSize: 15, display: "flex", alignItems: "center", gap: 12, background: bg, color }}>
      <span style={{ width: 22, height: 22, borderRadius: "50%", border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>!</span>
      {msg}
    </div>
  );
}

function StatusBar({ label, count, total, color }: { label: string; count: number; total: number; color: string }) {
  const w = total > 0 ? Math.max((count / total) * 100, 1) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 14, lineHeight: "20px" }}>
      <span style={{ width: 84, color: "#6b6b68", textAlign: "right", flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 16, background: "#f0efeb", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${w}%`, background: color, borderRadius: 8, minWidth: count > 0 ? 4 : 0 }} />
      </div>
      <span style={{ width: 32, fontWeight: 600, textAlign: "right", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{count}</span>
    </div>
  );
}

const PLAN_LABELS: Record<string, string> = { free: "Free", starter: "Starter", standard: "Professional", pro: "Enterprise" };

export default function DashboardTab({ onTabChange }: { onTabChange?: (tab: string) => void }) {
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [logs, setLogs] = useState<any[]>([]);

  useEffect(() => {
    Promise.all([
      api.getMe(),
      api.getMonthlyStats().catch(() => null),
      api.getLogs({ limit: "5" }).catch(() => ({ logs: [] })),
    ]).then(([t, s, l]) => {
      setTenant(t);
      setStats(s);
      setLogs(l.logs || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#6b6b68" }}>読み込み中...</div>;
  if (!tenant) return <div style={{ padding: 40, textAlign: "center", color: "#6b6b68" }}>テナント情報が取得できません</div>;

  const used = tenant.currentMonthUsage || 0;
  // 表示する上限は送信判定と同じ値（無料トライアル中は monthlyLimit が 0 のままのため）
  const limit = tenant.sendQuota?.effectiveMonthlyLimit ?? tenant.monthlyLimit ?? 0;
  const remaining = Math.max(limit - used, 0);
  // 送信件数は請求期間（契約日基準）ごとに数え直す。期間が分かるときは期間と次のリセット日を出す
  const period = usagePeriodLabel(tenant);
  const plan = tenant.plan || "free";
  const planLabel = tenant.sendQuota?.signupTrial?.active
    ? "無料トライアル（Professional 相当）"
    : PLAN_LABELS[plan] || plan;

  const cur = stats?.current || { total: 0, completed: 0, completionRate: 0, avgDays: 0, rejected: 0, distribution: {} };
  const prev = stats?.previous || { total: 0, completed: 0, completionRate: 0, avgDays: 0, rejected: 0 };
  const dist = cur.distribution || {};
  const total = Object.values(dist).reduce((a: number, b: any) => a + (b as number), 0) as number;

  const diffPct = prev.total > 0 ? Math.round(((cur.total - prev.total) / prev.total) * 100) : 0;

  const infoRow = (label: string, value: React.ReactNode) => (
    <div style={{ display: "grid", gridTemplateColumns: "72px 1fr", alignItems: "baseline", columnGap: 12 }}>
      <strong style={{ color: "#1a1a1a", fontWeight: 600 }}>{label}</strong>
      <span>{value}</span>
    </div>
  );

  const cell: React.CSSProperties = { padding: "12px 12px", borderBottom: "1px solid #f0efeb", verticalAlign: "middle" };

  return (
    <>
      <AnnouncementToast />
      <AnnouncementBanner />

      {/* カードどうしの縦の間隔は GAP に統一する（以前は 20 / 28 が混在していた） */}
      <div style={{ display: "flex", flexDirection: "column", gap: GAP }}>
        <AlertBanner used={used} limit={limit} />

        <div style={grid12}>
          <div style={span(3)}>
            <MetricCard label="今月の送信件数" value={cur.total} sub={prev.total > 0 ? `${diffPct >= 0 ? "+" : ""}${diffPct}% 前月比` : undefined} positive={diffPct >= 0} />
          </div>
          <div style={span(3)}>
            <MetricCard label="締結完了率" value={`${cur.completionRate}%`} sub={prev.completionRate > 0 ? `${cur.completionRate - prev.completionRate >= 0 ? "+" : ""}${cur.completionRate - prev.completionRate}pt 前月比` : undefined} positive={cur.completionRate >= prev.completionRate} />
          </div>
          <div style={span(3)}>
            <MetricCard label="平均締結日数" value={cur.avgDays > 0 ? `${cur.avgDays}日` : "—"} sub={prev.avgDays > 0 ? `${(cur.avgDays - prev.avgDays) >= 0 ? "+" : ""}${(cur.avgDays - prev.avgDays).toFixed(1)}日 前月比` : undefined} positive={cur.avgDays <= prev.avgDays} />
          </div>
          <div style={span(3)}>
            <MetricCard label="却下・期限切れ" value={`${cur.rejected}件`} sub={prev.rejected > 0 ? `${cur.rejected - prev.rejected}件 前月比` : undefined} positive={cur.rejected <= prev.rejected} />
          </div>
        </div>

        {/* 使用量とステータス分布は同じ高さに揃え、使用量の中身は縦方向の中央に置く */}
        <div style={grid12}>
          <div style={{ ...card, ...span(6), display: "flex", flexDirection: "column" }}>
            <CardTitle>{period ? `使用量（${period.range}）` : "使用量"}</CardTitle>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 32 }}>
              <GaugeRing used={used} limit={limit} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, fontSize: 15, lineHeight: "22px", color: "#6b6b68" }}>
                {infoRow("プラン", planLabel)}
                {infoRow("月間上限", limit > 0 ? (limit >= 999999 ? "無制限" : `${limit}件`) : "—")}
                {infoRow("残り", limit > 0 && limit < 999999 ? `${remaining}件` : "—")}
                {period && infoRow("リセット", `${period.nextReset}`)}
              </div>
            </div>
          </div>
          <div style={{ ...card, ...span(6) }}>
            <CardTitle>ステータス分布（今月）</CardTitle>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {STATUSES.map((s) => (
                <StatusBar key={s.key} label={s.label} count={dist[s.key] || 0} total={total} color={s.color} />
              ))}
            </div>
          </div>
        </div>

        <div style={card}>
          <CardTitle
            action={
              <span onClick={() => onTabChange?.("logs")} style={{ fontSize: 14, color: "#534AB7", cursor: "pointer" }}>すべて表示 →</span>
            }
          >
            最近の送信
          </CardTitle>
          {logs.length > 0 ? (
            <table style={{ width: "100%", fontSize: 15, borderCollapse: "collapse", tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "28%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "28%" }} />
              </colgroup>
              <thead>
                <tr>
                  {["レコードID", "オブジェクト", "ステータス", "送信日時"].map((h) => (
                    <th key={h} style={{ textAlign: "left", fontWeight: 500, color: "#6b6b68", padding: "8px 12px", borderBottom: "1px solid #e8e7e3", fontSize: 14 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log: any) => {
                  const sentDate = log.sentAt ? new Date(log.sentAt).toLocaleDateString("ja-JP") : "—";
                  const sentTime = log.sentAt ? new Date(log.sentAt).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" }) : "";
                  return (
                    <tr key={log.id}>
                      <td style={{ ...cell, fontFamily: "monospace", fontSize: 13, color: "#6b6b68" }}>{log.hubspotRecordId}</td>
                      <td style={{ ...cell, color: "#6b6b68" }}>{log.hubspotObjectType}</td>
                      <td style={cell}><Pill status={log.status} /></td>
                      <td style={{ ...cell, color: "#6b6b68", fontVariantNumeric: "tabular-nums" }}>{sentDate} {sentTime}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 24, textAlign: "center", color: "#6b6b68" }}>送信ログはまだありません</div>
          )}
        </div>

        <div style={grid12}>
          {[
            { title: "接続設定", desc: "クラウドサイン接続・プロパティ・送信設定", tab: "connection" },
            { title: "HubSpot ユーザー権限", desc: "誰がどのクライアントIDで送れるか", tab: "hubspot-users" },
            { title: "プラン・請求", desc: "プランの変更・請求情報", tab: "billing" },
          ].map((link) => (
            <div
              key={link.title}
              onClick={() => onTabChange?.(link.tab)}
              style={{ ...filledCard, ...span(4), display: "flex", flexDirection: "column", gap: 4, fontSize: 14, lineHeight: "20px", color: "#6b6b68", cursor: "pointer" }}
            >
              <div style={{ fontWeight: 600, color: "#1a1a1a", fontSize: 16, lineHeight: "24px" }}>{link.title}</div>
              {link.desc}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
